import { plainToInstance } from 'class-transformer'
import { Guid } from 'guid-typescript'
import { Node } from 'rete'

import { BaseFlowButtonWithOutput } from '../../../../../../../../../NewMessageEditors/NewMessageEditorWithMedia/ButtonsConstructor/Buttons/FlowButtons/BaseFlowButtonWithOutput'
import MessageEditorWithMediaData from '../../../../../../../../../NewMessageEditors/NewMessageEditorWithMedia/types'
import { SocketNames, SocketsActionsTypes } from '../../../../../../../components'
import CustomEditor from '../../../../../../Editor/CustomEditor'
import { CustomNode } from '../../../../../index'
import ActionNodeAction from '../../../../Action/logic/Actions'
import { NotifyGroupAction } from '../../../../Action/logic/Actions/NotifyGroupAction/NotifyGroupAction'
import { NotifyUsersAction } from '../../../../Action/logic/Actions/NotifyUsersAction/NotifyUsersAction'
import { SendMessageNode } from '../../SendMessageNode'
import UserInputAction from '../../actions/UserInputAction'
import { MessageNodeActionTypes, SendMessageNodeProps } from '../../types'
import ProxySendMessageNodeAction from './ProxySendMessageNodeAction'

export default class ProxyUserInputAction implements ProxySendMessageNodeAction {
  action: UserInputAction

  guid: string = Guid.create().toString()

  nodeProps: SendMessageNodeProps
  private socketsActions: SocketsActionsTypes

  constructor(action: UserInputAction, socketsActions: SocketsActionsTypes, nodeProps: SendMessageNodeProps) {
    this.action = action
    this.nodeProps = nodeProps
    this.socketsActions = socketsActions

    action.actions.forEach((a) => {
      if ((a instanceof NotifyUsersAction || a instanceof NotifyGroupAction) && a.variants.length) {
        a.variants[0].buttons.forEach((r) => r.forEach((b) => {
          if (b instanceof BaseFlowButtonWithOutput && b.output) {
            socketsActions.createOutput(b.output)
          }
        }))
      }
    })

    socketsActions.createOutput(action.fail_output, SocketNames.Fail)
    socketsActions.createOutput(action.inactive_action.output, SocketNames.Inactive)
  }

  setPropertyIndex(index: number): void {
    this.action.property_index = index
  }

  setFailVariants(messages: Array<MessageEditorWithMediaData>): void {
    this.action.fail_variants = messages
  }

  setFailTryMaxCount(count: number): void {
    this.action.fail_try_max_count = count
  }

  setPattern(val: string): void {
    this.action.pattern = val
  }

  setNegate(val: boolean): void {
    this.action.negate = val
  }

  async setUserInputActions(actions: Array<ActionNodeAction>, node: SendMessageNode, editor: CustomEditor): Promise<void> {
    const guides = actions.map((a) => a.guid)

    actions.forEach((a) => {
      if (a instanceof NotifyGroupAction || a instanceof NotifyUsersAction) {
        this.setMessages(a.variants)
      }
    })

    this.action.actions.forEach((a) => {
      if ((a instanceof NotifyGroupAction || a instanceof NotifyUsersAction) && !guides.includes(a.guid)) {
        a.variants[0]?.buttons.forEach((r) => r.forEach((b) => {
          if (b instanceof BaseFlowButtonWithOutput && b.output) {
            this.socketsActions.deleteOutput(b.output)
          }
        }))
      }
    })

    this.action.actions = actions

    await node.update()
    editor.view.updateConnections({ node: node as unknown as Node })
  }

  setMessages(messages: Array<MessageEditorWithMediaData>): void {
    const availableButtons: Array<number> = []
    const movedSockets: Array<number> = []

    this.action.fail_variants[0]?.buttons.forEach((r) => {
      r.forEach((b) => {
        if (b instanceof BaseFlowButtonWithOutput && b.output) {
          availableButtons.push(b.output)
        }
      })
    })

    const newButtons: Array<number> = []
    messages[0]?.buttons.forEach((r) => {
      r.forEach((b) => {
        if (b instanceof BaseFlowButtonWithOutput) {
          if (!b.output) {
            b.output = this.socketsActions.createOutput()
          }

          this.socketsActions.switchIsMoved(b.output, true)

          movedSockets.push(b.output)
          newButtons.push(b.output)
        }
      })
    })

    newButtons.forEach((b) => {
      if (!availableButtons.includes(b) && !this.socketsActions.checkOutput(b)) {
        this.socketsActions.createOutput(b)

        availableButtons.splice(availableButtons.indexOf(b), 1)
      }
    })

    requestAnimationFrame(() => movedSockets.forEach((id) => this.socketsActions.switchIsMoved(id, false)))

    requestAnimationFrame(() => {
      // eslint-disable-next-line sonarjs/no-identical-functions
      availableButtons.forEach((b) => {
        this.socketsActions.deleteOutput(b)
      })
    })
  }

  static getDefaultActionModel(): UserInputAction {
    return plainToInstance(UserInputAction, {
      type: MessageNodeActionTypes.UserInput,
      property_index: null,
      negate: false,
      pattern: '',
      fail_try_max_count: 0,
      fail_variants: [],
      inactive_action: {
        output: CustomNode.LastSocketId += 1,
        time: 0,
      },
      fail_output: CustomNode.LastSocketId += 1,
      actions: []
    })
  }

  getModel(): UserInputAction {
    const bodyToInstance = { ...this.action }

    if (!this.action.pattern || !this.action.pattern.trim()) {
      delete bodyToInstance.pattern
    }

    return plainToInstance(UserInputAction, bodyToInstance)
  }
}
